import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const fmtDT = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
      timeZone: "Europe/Rome",
    });
  } catch { return iso; }
};

export default function AlunniAccreditatiPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [q, setQ] = useState("");
  const [onlyLinked, setOnlyLinked] = useState(false);
  const [linkModal, setLinkModal] = useState(null);  // {user, query, results, loading}
  const [disableModal, setDisableModal] = useState(null);  // {user, motivo}

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await api.get("/admin/alunni-accreditati");
      setItems(r.data?.data || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const qn = q.trim().toLowerCase();
    return items.filter(u => {
      if (onlyLinked && !u.alunno_cod) return false;
      if (!qn) return true;
      const hay = [u.email, u.name, u.given_name, u.family_name, u.codice_fiscale, String(u.alunno_cod || "")]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(qn);
    });
  }, [items, q, onlyLinked]);

  const linked_count = items.filter(u => u.alunno_cod).length;

  // === Azioni ===
  const openLinkModal = (u) => setLinkModal({ user: u, query: u.codice_fiscale || u.family_name || "", results: [], loading: false });
  const closeLinkModal = () => setLinkModal(null);
  const searchAlunni = async () => {
    if (!linkModal?.query?.trim()) return;
    setLinkModal(m => ({ ...m, loading: true }));
    try {
      const r = await api.get("/fillout/alunni-search", { params: { q: linkModal.query, limit: 30 } });
      setLinkModal(m => ({ ...m, results: r.data?.data || [], loading: false }));
    } catch (e) {
      setLinkModal(m => ({ ...m, loading: false }));
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore ricerca" });
    }
  };
  const linkTo = async (alunno) => {
    try {
      const r = await api.post(`/admin/alunni-accreditati/${linkModal.user._id}/link`, { alunno_cod: alunno.Cod });
      setMsg({ ok: true, text: `Collegato a ${r.data.alunno_nome} (Cod ${r.data.alunno_cod})` });
      closeLinkModal();
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore collegamento" });
    }
  };
  const unlink = async (u) => {
    if (!window.confirm(`Scollegare ${u.email} dal Cod ${u.alunno_cod}?`)) return;
    try {
      await api.post(`/admin/alunni-accreditati/${u._id}/unlink`);
      setMsg({ ok: true, text: "Collegamento rimosso" });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    }
  };

  const openDisableModal = (u) => setDisableModal({ user: u, motivo: "" });
  const closeDisableModal = () => setDisableModal(null);
  const doDisable = async () => {
    try {
      await api.post(`/admin/alunni-accreditati/${disableModal.user._id}/disable`, { motivo: disableModal.motivo });
      setMsg({ ok: true, text: `${disableModal.user.email} disabilitato` });
      closeDisableModal();
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    }
  };
  const enable = async (u) => {
    if (!window.confirm(`Riabilitare ${u.email}?`)) return;
    try {
      await api.post(`/admin/alunni-accreditati/${u._id}/enable`);
      setMsg({ ok: true, text: "Utente riabilitato" });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    }
  };
  const removeAlunno = async (u) => {
    if (!window.confirm(`Cancellare DEFINITIVAMENTE l'account ${u.email}?\n\nQuesta operazione è IRREVERSIBILE.\nL'utente potrà ri-accreditarsi al portale rifacendo login con Google e inserendo il proprio CF.`)) return;
    try {
      await api.delete(`/admin/alunni-accreditati/${u._id}`);
      setMsg({ ok: true, text: `${u.email} cancellato definitivamente` });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    }
  };
  const promoteToStaff = async (u) => {
    if (!window.confirm(
      `🔄 Ripristinare ${u.email} come utente STAFF?\n\n` +
      `Tipico per chi è entrato per errore nel portale alunni.\n` +
      `Lo trasforma da "alunno" → "operatore" (senza permessi/gestioni).\n` +
      `Poi dovrai andare in Utenti → Modifica per assegnare i permessi corretti.\n\n` +
      `Procedo?`
    )) return;
    try {
      const r = await api.post(`/admin/alunni-accreditati/${u._id}/promote-to-staff`);
      setMsg({ ok: true, text: r.data?.message || "Ripristinato come staff. Ora compare in Utenti." });
      load();
    } catch (e) {
      setMsg({ ok: false, text: e.response?.data?.detail || "Errore" });
    }
  };

  return (
    <div data-testid="alunni-accreditati-page">
      <div className="sw-page-title">
        <h1>Alunni Accreditati al Portale</h1>
        <div className="sw-breadcrumb">Home &gt; Sistema &gt; Alunni Accreditati</div>
      </div>

      {msg && <div style={{ margin: "8px 20px", padding: "8px 12px", background: msg.ok ? "#eeffee" : "#ffeeee", border: `1px solid ${msg.ok ? "#aea" : "#eaa"}`, fontSize: 12, fontWeight: 600, color: msg.ok ? "#060" : "#c00" }} data-testid="msg-banner">{msg.text}</div>}

      <div style={{ display: "flex", gap: 12, margin: "12px 20px 4px", flexWrap: "wrap" }}>
        <div style={{ minWidth: 160, padding: "10px 16px", background: "#e3f2fd", border: "1px solid #1976d2" }} data-testid="stat-total">
          <div style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>Totale accreditati</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1565c0" }}>{items.length}</div>
        </div>
        <div style={{ minWidth: 160, padding: "10px 16px", background: "#e8f5e9", border: "1px solid #2e7d32" }} data-testid="stat-linked">
          <div style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>Con CF collegato</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#1b5e20" }}>{linked_count}</div>
        </div>
        <div style={{ minWidth: 160, padding: "10px 16px", background: "#fff3e0", border: "1px solid #ef6c00" }} data-testid="stat-not-linked">
          <div style={{ fontSize: 11, color: "#666", fontWeight: 600 }}>Senza collegamento</div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#e65100" }}>{items.length - linked_count}</div>
        </div>
      </div>

      <div className="sw-filter-row" style={{ margin: "10px 20px" }}>
        <span className="sw-filter-label">Cerca:</span>
        <input data-testid="filter-search" type="text" value={q} onChange={e => setQ(e.target.value)}
          placeholder="email, nome, CF, codice alunno..."
          className="sw-filter-input" style={{ width: 280 }} />
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, marginLeft: 12, cursor: "pointer" }}>
          <input type="checkbox" data-testid="filter-only-linked" checked={onlyLinked} onChange={e => setOnlyLinked(e.target.checked)} />
          Solo con CF collegato
        </label>
        <button className="sw-filter-btn" onClick={load} data-testid="btn-refresh" style={{ marginLeft: "auto" }}>↻ Aggiorna</button>
      </div>

      {err && <div style={{ margin: "8px 20px", padding: "8px 12px", background: "#ffeeee", border: "1px solid #eaa", color: "#c00", fontSize: 12, fontWeight: 600 }}>{err}</div>}

      <div className="sw-table-container" style={{ overflowX: "auto" }}>
        <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
          <thead>
            <tr>
              <th style={{ width: 36, textAlign: "center" }}>#</th>
              <th style={{ minWidth: 220 }}>Email</th>
              <th style={{ minWidth: 160 }}>Nome</th>
              <th style={{ minWidth: 130 }}>Codice Fiscale</th>
              <th style={{ width: 90, textAlign: "center" }}>Cod. Alunno</th>
              <th style={{ minWidth: 130 }}>Primo accesso</th>
              <th style={{ minWidth: 130 }}>Ultimo accesso</th>
              <th style={{ width: 100, textAlign: "center" }}>Stato</th>
              <th style={{ width: 200, textAlign: "center" }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="sw-loading">Caricamento...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign: "center", padding: 24, color: "#999" }}>
                Nessun alunno accreditato {q && "corrisponde ai filtri"}.
              </td></tr>
            ) : filtered.map((u, i) => {
              const isDisabled = u.status === "disabled";
              return (
                <tr key={u._id} data-testid={`alunno-row-${i}`} style={{ opacity: isDisabled ? 0.55 : 1 }}>
                  <td className="center-col" style={{ color: "#888", fontFamily: "Consolas, monospace" }}>{i + 1}</td>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {u.photo_url && <img src={u.photo_url} alt="" style={{ width: 22, height: 22, borderRadius: "50%" }} />}
                      <b>{u.email}</b>
                    </div>
                  </td>
                  <td>{u.name || `${u.given_name || ""} ${u.family_name || ""}`.trim() || "—"}</td>
                  <td style={{ fontFamily: "Consolas, monospace", fontSize: 10 }}>{u.codice_fiscale || "—"}</td>
                  <td style={{ textAlign: "center", fontWeight: 700, color: u.alunno_cod ? "#1565c0" : "#bbb" }}>
                    {u.alunno_cod || "—"}
                  </td>
                  <td style={{ fontSize: 10, color: "#333" }} data-testid={`alunno-first-access-${i}`}>{fmtDT(u.created_at)}</td>
                  <td style={{ fontSize: 10, color: "#666" }}>{fmtDT(u.last_login_at)}</td>
                  <td style={{ textAlign: "center" }}>
                    {isDisabled ? (
                      <span style={{ background: "#ffebee", color: "#c62828", padding: "2px 8px", fontSize: 10, fontWeight: 700, borderRadius: 10 }} title={u.disabled_reason || ""}>🚫 Disabilitato</span>
                    ) : u.alunno_cod ? (
                      <span style={{ background: "#e8f5e9", color: "#1b5e20", padding: "2px 8px", fontSize: 10, fontWeight: 700, borderRadius: 10 }}>✓ Collegato</span>
                    ) : (
                      <span style={{ background: "#fff3e0", color: "#e65100", padding: "2px 8px", fontSize: 10, fontWeight: 700, borderRadius: 10 }}>⏳ Non collegato</span>
                    )}
                  </td>
                  <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                    {!u.alunno_cod && !isDisabled && (
                      <button onClick={() => openLinkModal(u)} data-testid={`btn-link-${i}`}
                        style={{ border: "1px solid #1565c0", background: "white", color: "#1565c0", cursor: "pointer", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 2, marginRight: 4 }}>
                        🔗 Collega
                      </button>
                    )}
                    {u.alunno_cod && !isDisabled && (
                      <button onClick={() => unlink(u)} data-testid={`btn-unlink-${i}`}
                        title="Rimuovi collegamento"
                        style={{ border: "1px solid #ef6c00", background: "white", color: "#ef6c00", cursor: "pointer", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 2, marginRight: 4 }}>
                        ✕ Scollega
                      </button>
                    )}
                    {isDisabled ? (
                      <button onClick={() => enable(u)} data-testid={`btn-enable-${i}`}
                        style={{ border: "1px solid #2e7d32", background: "white", color: "#2e7d32", cursor: "pointer", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 2, marginRight: 4 }}>
                        ✓ Riabilita
                      </button>
                    ) : (
                      <button onClick={() => openDisableModal(u)} data-testid={`btn-disable-${i}`}
                        style={{ border: "1px solid #c62828", background: "white", color: "#c62828", cursor: "pointer", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 2, marginRight: 4 }}>
                        🚫 Disabilita
                      </button>
                    )}
                    <button
                      data-testid={`btn-toggle-test-${i}`}
                      onClick={async () => {
                        const next = !u.is_test_user;
                        if (!window.confirm(next
                          ? `Marcare "${u.email}" come UTENTE DI PROVA?\n\nPotrà accedere al Portale Alunni Extra e SPOT anche quando i portali sono chiusi.`
                          : `Rimuovere il flag "Utente di prova" da "${u.email}"?`)) return;
                        try {
                          await api.put(`/users/${u._id}/is-test-user`, { is_test_user: next });
                          load();
                        } catch (e) {
                          alert("Errore: " + (e.response?.data?.detail || e.message));
                        }
                      }}
                      title={u.is_test_user
                        ? "Utente di PROVA: può entrare nei portali anche se chiusi. Clicca per rimuovere."
                        : "Marca come utente di prova (bypassa la chiusura dei portali)"}
                      style={{ border: `1px solid ${u.is_test_user ? "#ef6c00" : "#999"}`,
                               background: u.is_test_user ? "#ef6c00" : "white",
                               color: u.is_test_user ? "white" : "#999",
                               cursor: "pointer", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 2, marginRight: 4 }}>
                      🧪 {u.is_test_user ? "PROVA ON" : "Prova"}
                    </button>
                    <button onClick={() => promoteToStaff(u)} data-testid={`btn-promote-staff-${i}`}
                      title="🔄 Ripristina come utente STAFF (per chi è entrato per errore nel portale alunni). Lo trasforma da 'alunno' a 'operatore' e lo restituisce alla lista Utenti."
                      style={{ border: "1px solid #1565c0", background: "#1565c0", color: "white", cursor: "pointer", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 2, marginRight: 4 }}>
                      🔄 Staff
                    </button>
                    <button onClick={() => removeAlunno(u)} data-testid={`btn-delete-${i}`}
                      title="Cancella definitivamente l'account (irreversibile)"
                      style={{ border: "1px solid #b71c1c", background: "#b71c1c", color: "white", cursor: "pointer", fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 2 }}>
                      🗑
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Modal: Collega manualmente */}
      {linkModal && (
        <div onClick={closeLinkModal} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="link-modal" style={{ background: "white", width: "100%", maxWidth: 720, borderRadius: 4, overflow: "hidden", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "#1565c0", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700 }}>🔗 Collega: {linkModal.user.email}</span>
              <button onClick={closeLinkModal} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 20, overflowY: "auto" }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <input data-testid="link-search-input" type="text" value={linkModal.query}
                  onChange={e => setLinkModal(m => ({ ...m, query: e.target.value }))}
                  onKeyDown={e => { if (e.key === "Enter") searchAlunni(); }}
                  placeholder="Cognome, nome o codice alunno..."
                  className="sw-filter-input" style={{ flex: 1 }} autoFocus />
                <button data-testid="btn-search-alunno" onClick={searchAlunni} className="sw-filter-btn sw-filter-btn-primary">
                  {linkModal.loading ? "..." : "🔍 Cerca"}
                </button>
              </div>
              {linkModal.results.length === 0 && !linkModal.loading && (
                <div style={{ color: "#999", fontSize: 12, textAlign: "center", padding: 20 }}>
                  Inserisci un cognome/nome/codice e premi Cerca.
                </div>
              )}
              {linkModal.results.length > 0 && (
                <table className="sw-table sw-table-compact" style={{ fontSize: 11 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 60, textAlign: "center" }}>Cod</th>
                      <th>Cognome Nome</th>
                      <th style={{ width: 100 }}>Classe</th>
                      <th style={{ width: 70, textAlign: "center" }}>Settore</th>
                      <th style={{ width: 90, textAlign: "center" }}>Azione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkModal.results.map((a) => (
                      <tr key={a.Cod} data-testid={`link-result-${a.Cod}`}>
                        <td style={{ textAlign: "center", fontWeight: 700, color: "#1565c0" }}>{a.Cod}</td>
                        <td style={{ fontWeight: 600 }}>{a.nome_completo}</td>
                        <td>{a.classe || "—"}</td>
                        <td style={{ textAlign: "center" }}>{a.settore || "—"}</td>
                        <td style={{ textAlign: "center" }}>
                          <button onClick={() => linkTo(a)} data-testid={`btn-link-confirm-${a.Cod}`}
                            style={{ border: "1px solid #2e7d32", background: "#e8f5e9", color: "#2e7d32", cursor: "pointer", fontSize: 10, fontWeight: 700, padding: "2px 10px", borderRadius: 2 }}>
                            ✓ Collega
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal: Disabilita */}
      {disableModal && (
        <div onClick={closeDisableModal} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="disable-modal" style={{ background: "white", width: "100%", maxWidth: 460, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ background: "#c62828", color: "white", padding: "12px 16px", display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontWeight: 700 }}>🚫 Disabilita: {disableModal.user.email}</span>
              <button onClick={closeDisableModal} style={{ background: "none", border: "none", color: "white", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>
            <div style={{ padding: 20 }}>
              <p style={{ fontSize: 12, color: "#555", marginTop: 0 }}>
                L'utente <b>{disableModal.user.email}</b> non potrà più accedere al portale alunni. Puoi riabilitarlo in qualsiasi momento.
              </p>
              <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#666", marginBottom: 4, marginTop: 12 }}>Motivo (opzionale)</label>
              <textarea data-testid="disable-motivo"
                value={disableModal.motivo}
                onChange={e => setDisableModal(m => ({ ...m, motivo: e.target.value }))}
                placeholder="es. Anno scolastico concluso, account duplicato, ecc."
                style={{ width: "100%", minHeight: 60, fontSize: 12, padding: 6, fontFamily: "inherit" }} />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
                <button onClick={closeDisableModal} className="sw-filter-btn">Annulla</button>
                <button onClick={doDisable} data-testid="btn-confirm-disable"
                  style={{ background: "#c62828", color: "white", border: "none", padding: "6px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                  🚫 Disabilita
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
